import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert, Chip, Divider, Collapse,
} from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { phappsRequest } from "@/services/apiService";
import { buildDialpadPath, API_PATHS } from "@/config/apiConstants";
import CallDetailModal from "@/components/admin/dialpad/CallDetailModal";
import SavePhoneDialog from "@/components/admin/dialpad/SavePhoneDialog";

const DIALPAD_PINK = "#E63888";

function fmtDuration(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// ── Reusable call row ─────────────────────────────────────────────────────────
function CallRow({ call, onView, action }) {
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 1.5, borderRadius: 1.5,
        border: "1px solid #e5e7eb", backgroundColor: "#fff",
        "&:hover": { borderColor: DIALPAD_PINK, backgroundColor: "#FDE8F3" },
        transition: "all 0.12s",
      }}
    >
      <Box sx={{ color: call.direction === "inbound" ? "#059669" : "#2563eb", flexShrink: 0 }}>
        {call.direction === "inbound" ? <CallReceivedIcon fontSize="small" /> : <CallMadeIcon fontSize="small" />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {call.external_number || "Unknown"}
          </Typography>
          {!!call.has_ai_summary && (
            <Chip label="AI" size="small" sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "#ede9fe", color: DIALPAD_PINK }} />
          )}
          {!!call.auto_matched && (
            <Chip label="Auto" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {fmtDateTime(call.start_time)} · {fmtDuration(call.duration_seconds)}
          {call.manager_name ? ` · ${call.manager_name}` : ""}
          {call.card_title ? ` · ${call.card_title}` : ""}
        </Typography>
        {call.ai_summary && (() => {
          try {
            const parsed = JSON.parse(call.ai_summary);
            const text = parsed?.summary ?? parsed?.body ?? parsed?.content ?? null;
            if (!text) return null;
            return (
              <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mt: 0.25 }} noWrap>
                {text.length > 120 ? text.slice(0, 120) + "…" : text}
              </Typography>
            );
          } catch { return null; }
        })()}
      </Box>
      {onView && (
        <Button
          size="small"
          startIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
          onClick={() => onView(call)}
          sx={{ textTransform: "none", fontSize: "0.7rem", color: DIALPAD_PINK, flexShrink: 0, px: 1 }}
        >
          View
        </Button>
      )}
      {action}
    </Box>
  );
}

// ── Main tab ─────────────────────────────────────────────────────────────────
const ChemistCallsTab = ({ chemistId, chemistUUID, chemistName }) => {
  // ── Call history ──
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openCall, setOpenCall] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // ── Unallocated section ──
  const [showUnallocated, setShowUnallocated] = useState(false);
  const [unallocated, setUnallocated] = useState([]);
  const [unallocatedLoading, setUnallocatedLoading] = useState(false);
  const [unallocatedError, setUnallocatedError] = useState(null);
  const [allocating, setAllocating] = useState(null); // callId being allocated
  const [justAllocated, setJustAllocated] = useState(new Set()); // ids just confirmed
  const [savePhoneCall, setSavePhoneCall] = useState(null); // call to prompt saving number for

  // ── Load call history ──
  const loadHistory = useCallback(async (pg = 1) => {
    if (!chemistId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await phappsRequest(
        buildDialpadPath(`calls/by-chemist/${chemistId}?page=${pg}&limit=50`), "GET"
      );
      const rows = data ?? [];
      if (pg === 1) setCalls(rows);
      else setCalls(prev => [...prev, ...rows]);
      setHasMore(rows.length === 50);
    } catch {
      setError("Failed to load call history");
    } finally {
      setLoading(false);
    }
  }, [chemistId]);

  useEffect(() => { setPage(1); loadHistory(1); }, [loadHistory]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    loadHistory(next);
  };

  // ── Load unallocated calls ──
  const loadUnallocated = useCallback(async () => {
    setUnallocatedLoading(true);
    setUnallocatedError(null);
    try {
      const data = await phappsRequest(
        `${API_PATHS.DIALPAD_CALLS_UNALLOCATED}?page=1&limit=100`, "GET"
      );
      setUnallocated(data?.calls ?? []);
    } catch {
      setUnallocatedError("Failed to load unallocated calls");
    } finally {
      setUnallocatedLoading(false);
    }
  }, []);

  const handleToggleUnallocated = () => {
    if (!showUnallocated) loadUnallocated();
    setShowUnallocated(v => !v);
    setJustAllocated(new Set());
  };

  // ── Allocate a call to this chemist ──
  const handleAllocate = async (call) => {
    setAllocating(call.id);
    try {
      await phappsRequest(
        buildDialpadPath(`calls/${call.id}/allocate`), "POST", { chemist_id: chemistId }
      );
      // Mark as just-allocated so we can show confirmation before removing
      setJustAllocated(prev => new Set([...prev, call.id]));
      // After a short delay, remove from the unallocated list and refresh history
      setTimeout(() => {
        setUnallocated(prev => prev.filter(c => c.id !== call.id));
        setJustAllocated(prev => { const n = new Set(prev); n.delete(call.id); return n; });
        setPage(1);
        loadHistory(1);
      }, 1200);
      // Prompt to save phone number if we have a UUID and a number
      if (chemistUUID && call.external_number) {
        setSavePhoneCall(call);
      }
    } catch {
      setUnallocatedError("Failed to allocate call. Please try again.");
    } finally {
      setAllocating(null);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

      {/* ── Call history header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: DIALPAD_PINK }}>
          Call History
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {calls.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {calls.length} call{calls.length !== 1 ? "s" : ""} shown
            </Typography>
          )}
          {/* Toggle unallocated section */}
          <Button
            size="small"
            variant={showUnallocated ? "contained" : "outlined"}
            startIcon={showUnallocated ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <LinkIcon sx={{ fontSize: 15 }} />}
            onClick={handleToggleUnallocated}
            sx={{
              textTransform: "none", fontSize: "0.72rem", fontWeight: 600,
              ...(showUnallocated
                ? { backgroundColor: DIALPAD_PINK, "&:hover": { backgroundColor: "#c42d72" } }
                : { borderColor: DIALPAD_PINK, color: DIALPAD_PINK, "&:hover": { backgroundColor: "#FDE8F3", borderColor: DIALPAD_PINK } }
              ),
            }}
          >
            {showUnallocated ? "Hide unallocated" : "Link unallocated calls"}
          </Button>
        </Box>
      </Box>

      {/* ── Unallocated calls section ── */}
      <Collapse in={showUnallocated}>
        <Box
          sx={{
            border: "1px dashed #fbbf24",
            borderRadius: 2,
            backgroundColor: "#fffbeb",
            p: 1.5,
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Unallocated calls — click to assign to this chemist
            </Typography>
            <Button
              size="small"
              onClick={loadUnallocated}
              disabled={unallocatedLoading}
              sx={{ textTransform: "none", color: "#92400e", fontSize: "0.68rem", py: 0, minWidth: 0 }}
            >
              {unallocatedLoading ? "Refreshing…" : "Refresh"}
            </Button>
          </Box>

          {unallocatedError && (
            <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>{unallocatedError}</Alert>
          )}

          {unallocatedLoading && unallocated.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} sx={{ color: DIALPAD_PINK }} />
            </Box>
          ) : unallocated.length === 0 ? (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                No unallocated calls found.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {unallocated.map(call => {
                const isAllocating = allocating === call.id;
                const isDone = justAllocated.has(call.id);
                return (
                  <Box
                    key={call.id}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.5,
                      p: 1.25, borderRadius: 1.5,
                      border: isDone ? "1px solid #6ee7b7" : "1px solid #fde68a",
                      backgroundColor: isDone ? "#ecfdf5" : "#fff",
                      transition: "all 0.2s",
                    }}
                  >
                    <Box sx={{ color: call.direction === "inbound" ? "#059669" : "#2563eb", flexShrink: 0 }}>
                      {call.direction === "inbound" ? <CallReceivedIcon fontSize="small" /> : <CallMadeIcon fontSize="small" />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {call.external_number || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDateTime(call.start_time)} · {fmtDuration(call.duration_seconds)}
                        {call.manager_name ? ` · ${call.manager_name}` : ""}
                      </Typography>
                    </Box>
                    {isDone ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#059669" }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Allocated</Typography>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isAllocating}
                        onClick={() => handleAllocate(call)}
                        startIcon={isAllocating
                          ? <CircularProgress size={12} sx={{ color: "#fff" }} />
                          : <LinkIcon sx={{ fontSize: "14px !important" }} />
                        }
                        sx={{
                          textTransform: "none", fontSize: "0.7rem", fontWeight: 600, flexShrink: 0,
                          backgroundColor: DIALPAD_PINK, "&:hover": { backgroundColor: "#c42d72" },
                        }}
                      >
                        {isAllocating ? "Allocating…" : "Allocate"}
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>

      {error && <Alert severity="warning">{error}</Alert>}

      {/* ── Call history list ── */}
      {loading && calls.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress size={28} sx={{ color: DIALPAD_PINK }} />
        </Box>
      ) : calls.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <PhoneOutlinedIcon sx={{ fontSize: 40, color: "#d1d5db", mb: 1 }} />
          <Typography color="text.secondary" variant="body2">No calls recorded for this pharmacy.</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {calls.map(call => (
              <CallRow key={call.id} call={call} onView={setOpenCall} />
            ))}
          </Box>

          {hasMore && (
            <Box sx={{ textAlign: "center" }}>
              <Button
                onClick={loadMore}
                disabled={loading}
                sx={{ textTransform: "none", color: DIALPAD_PINK }}
                size="small"
              >
                {loading ? "Loading…" : "Load more"}
              </Button>
            </Box>
          )}
        </>
      )}

      <CallDetailModal
        open={!!openCall}
        onClose={() => setOpenCall(null)}
        call={openCall}
      />

      <SavePhoneDialog
        open={!!savePhoneCall}
        onClose={() => setSavePhoneCall(null)}
        phone={savePhoneCall?.external_number}
        chemistUUID={chemistUUID}
        chemistName={chemistName}
      />
    </Box>
  );
};

export default ChemistCallsTab;
